/**
 * Post-sign-in routing decision tree, extracted from sign-in.vue's
 * `redirectAfterSuccessfulSignIn` for cas-da8c PR1.
 *
 * Pure helper — no Vue, no router, no Quasar, no async. Caller passes
 * pre-resolved auth state + workspace state + the (already-decoded)
 * returnUrl from the URL query, gets back a routing target. Caller maps
 * `{ path, query? }` onto router.push.
 *
 * Why extraction: the repo has no @vue/test-utils mount harness for
 * Quasar pages (verified — same constraint as cas-2627, cas-8d1e,
 * cas-d1ce, cas-b77b). Pulling the decision tree into a util keeps the
 * "explicit returnUrl wins over shouldRouteSignedInUserToStart"
 * invariant unit-testable.
 *
 * Decision priority (top wins):
 *   1. explicit returnUrl → returnUrl (skip if legacy /post-signup-onboarding)
 *   2. shouldRouteToStart → /start
 *   3. hasWorkspace       → / (Home) else /create-workspace
 *
 * Critically: returnUrl is checked BEFORE shouldRouteToStart. Pre-PR1
 * the order was reversed, causing legitimate deep-links (Ben's
 * /i/<itemId> case) to be silently dropped when the user happened to
 * have a returnable-to-start onboarding state.
 *
 * cas-9bcb: Priority 4 was previously keyed on `hasSocialHandles`
 * (Instagram/TikTok/Facebook/YouTube connected). That was a proxy for
 * workspace existence, but it caused returning users who had a workspace
 * with no social handles to be routed to /create-workspace?add=1 on
 * every sign-in. Replaced with `hasWorkspace` (workspaces.length > 0)
 * so workspace membership — not social-handle presence — is the gate.
 */

import { NON_DEFAULT_LOCALE_CODES } from './public-audit-path';

export interface PostSignInTarget {
  path: string;
  query?: Record<string, string | string[]>;
  hash?: string;
}

/**
 * cas-575f: vue-router ignores a query written inside `path`
 * (`router.push({ path: '/p/…?routine=…' })` lands on `/p/…` with no query),
 * so a returnUrl is split into path, query and hash before it is pushed.
 */
export const splitReturnUrl = (url: string): PostSignInTarget => {
  const hashAt = url.indexOf('#');
  const hash = hashAt >= 0 ? url.slice(hashAt) : '';
  const beforeHash = hashAt >= 0 ? url.slice(0, hashAt) : url;
  const queryAt = beforeHash.indexOf('?');
  const path = queryAt >= 0 ? beforeHash.slice(0, queryAt) : beforeHash;
  const target: PostSignInTarget = { path };
  if (queryAt >= 0) {
    const query: Record<string, string | string[]> = {};
    for (const [key, value] of new URLSearchParams(beforeHash.slice(queryAt + 1))) {
      const prior = query[key];
      query[key] =
        prior === undefined ? value : [...(Array.isArray(prior) ? prior : [prior]), value];
    }
    if (Object.keys(query).length > 0) target.query = query;
  }
  if (hash.length > 1) target.hash = hash;
  return target;
};

/**
 * Where a returning user with a workspace lands.
 *
 * Ben Q15: a returning user lands on Home. Exported so the middleware that
 * redirects an already-signed-in user cannot drift from the sign-in path.
 */
export const DEFAULT_SIGNED_IN_PATH = '/';

/**
 * cas-d666 / cas-1d8c: Home in the locale of the page the visitor signed in on.
 * `/es`, `/es/...` → `/es`; every other path (incl. `/espresso/...`) → `/`.
 * Shared by the sign-in guard's safe fallback and the post-sign-in default.
 */
export const localeHome = (path: string): string => {
  for (const code of NON_DEFAULT_LOCALE_CODES) {
    if (path === `/${code}` || path.startsWith(`/${code}/`)) return `/${code}`;
  }
  return DEFAULT_SIGNED_IN_PATH;
};

/**
 * cas-1ef2: an internal post-sign-in target in the sign-in page's locale.
 * `/start` from `/es/sign-in` → `/es/start`; from any unprefixed path → `/start`.
 * Only for targets the resolver chooses itself — an explicit returnUrl is the
 * visitor's own destination and is never rewritten.
 */
export const localeTarget = (path: string, signInPath: string | undefined): string => {
  const home = localeHome(signInPath ?? '');
  return home === DEFAULT_SIGNED_IN_PATH ? path : `${home}${path}`;
};

/**
 * cas-da8c PR1 review autofix (adversarial P2 / safe_auto): anchored
 * boundary match. The pre-autofix `startsWith('/post-signup-onboarding')`
 * also matched `/post-signup-onboarding-v2` and similar dash-suffixed
 * paths, silently dropping legitimate future returnUrls. Match exactly
 * the legacy path OR any sub-path with a real `/` separator.
 */
const isLegacyOnboardingPath = (path: string): boolean =>
  path === '/post-signup-onboarding' || path.startsWith('/post-signup-onboarding/');

/**
 * cas-da8c PR1 review autofix (security + testing P1/P2 cross-flagged):
 * reject returnUrl values that would route off-origin or to a non-route
 * scheme. Guards against:
 *   - Absolute URLs:           https://evil.com/phish
 *   - Protocol-relative URLs:  //evil.com/phish
 *   - Backslash-escaped URLs:  /\evil.com (Edge legacy quirk)
 *   - Dangerous schemes:       javascript:, data:, vbscript:, file:
 *
 * Vue Router's current behavior (v4.5.x) happens to normalize most of
 * these to same-origin pushState navigations, but that safety is an
 * undocumented implementation detail. A future router upgrade or SSR
 * adapter could turn any of these into real external navigation.
 * Defense-in-depth: only honor values that begin with a single `/`.
 */
const isSafeRelativePath = (path: string): boolean => {
  // Must start with exactly one `/` — rules out '//evil.com' (protocol-relative).
  if (!path.startsWith('/') || path.startsWith('//')) {
    return false;
  }
  // Backslash-escaped slashes (legacy Edge quirk): '/\evil.com'.
  if (path.startsWith('/\\')) {
    return false;
  }
  // Dangerous schemes — these can never appear as a leading-slash path,
  // but check defensively in case of upstream encoding bypass.
  const lower = path.toLowerCase();
  if (
    lower.includes('javascript:') ||
    lower.startsWith('/javascript:') ||
    lower.includes('vbscript:') ||
    lower.includes('data:') ||
    lower.startsWith('/data:')
  ) {
    return false;
  }
  return true;
};

export const normalizeReturnUrl = (raw: string | null | undefined): string | null => {
  if (raw == null) {
    return null;
  }
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return null;
  }
  if (!isSafeRelativePath(trimmed)) {
    return null;
  }
  return trimmed;
};

export const resolvePostSignInTarget = (args: {
  /**
   * Whether the existing onboarding-gate (shouldRouteSignedInUserToStart)
   * says this user belongs on /start (legacy onboarding state). PR1
   * changes the priority order so an explicit returnUrl OVERRIDES this.
   */
  shouldRouteToStart: boolean;
  /**
   * Pre-decoded returnUrl from `route.query.returnUrl`. Caller is
   * responsible for `decodeURIComponent`. Pass null/empty when absent.
   */
  returnUrl: string | null | undefined;
  /**
   * Whether the user already has at least one workspace. Drives the
   * default fallback between / (has workspace) and /create-workspace
   * (no workspace yet).
   *
   * cas-9bcb: replaces the old `hasSocialHandles` check which used
   * social-handle presence as a proxy for workspace existence. That caused
   * returning users with a workspace but no social handles (Instagram/
   * TikTok/Facebook/YouTube) to be re-prompted to create a workspace on
   * every sign-in. Workspace membership is the correct gate.
   */
  hasWorkspace: boolean;
  /**
   * cas-1d8c / cas-1ef2: the sign-in page's own path (`route.path`), so every
   * internal target keeps its locale (`/es/sign-in` → `/es`, `/es/start`,
   * `/es/create-workspace`). Omitted → the default-locale targets.
   */
  signInPath?: string;
}): PostSignInTarget => {
  // 1. Explicit returnUrl wins over shouldRouteToStart (cas-da8c PR1
  // fix). Skip when it points at the deprecated /post-signup-onboarding
  // path — those URLs are legacy and not honored.
  const returnUrl = normalizeReturnUrl(args.returnUrl);
  if (returnUrl && !isLegacyOnboardingPath(returnUrl)) {
    return splitReturnUrl(returnUrl);
  }

  // 2. shouldRouteToStart fallback when no explicit returnUrl.
  if (args.shouldRouteToStart) {
    return { path: localeTarget('/start', args.signInPath) };
  }

  // 3. Default fallthrough: if the user already has a workspace, send
  // them Home. If not, send them to create one. (cas-9bcb:
  // was `hasSocialHandles` — see jsdoc above for why this changed.)
  //
  // `?setup=1` is kept on the no-workspace path for defence-in-depth:
  // create-workspace.vue has an onMounted guard that redirects to brand
  // settings when settingsStore.workspace.current is populated. Without the
  // setup marker, a user whose Pinia/
  // localStorage still holds a stale workspace ref (e.g., from a
  // deleted workspace) would be silently bounced to brand settings
  // instead of seeing the creation form. Crucially, setup is distinct from
  // `?add=1`, which authorizes a deliberate additional workspace.
  if (args.hasWorkspace) {
    return { path: localeHome(args.signInPath ?? '') };
  }
  return { path: localeTarget('/create-workspace', args.signInPath), query: { setup: '1' } };
};
